import clsx from 'clsx';
import React from 'react';
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Label from '@/src/components/common/Elements/Label/Label';

import LineItemsForm, { TableEvents } from './LineItemsForm';

import Table from '../../../../Elements/Table/Table';
import { TaxCalculationEnum } from '../../../../models/Treatment/TreatmentCalculator';
import { RemoteOptions } from '../../../../types';
import { LineEntryForm } from '../../../../types/treatment';
import InputField from '../../Fields/InputField/InputField';
import NumericInputField from '../../Fields/NumericInputField/NumericInputField';
import SelectField, { renderWithTypeOptions } from '../../Fields/SelectField/SelectField';

const getSelectTaxtooltip = (rowIndex: number, isTaxRequired: boolean) => {
  if (!isTaxRequired) {
    return {
      id: `lineItems.${rowIndex}.taxCode`,
      content: 'This field is disabled because you selected Out of Scope Tax',
    };
  }

  return null;
};

export const getIsTaxRequired = (taxCalculation: FieldValues) => {
  return taxCalculation.value
    ? [TaxCalculationEnum.TaxInclusive, TaxCalculationEnum.TaxExcluded].includes(
        taxCalculation.value
      )
    : false;
};

const paddingClasses = 'tw-py-[5px] tw-px-[5px]';
const headerRowClasses = 'tw-pl-[15px] tw-pr-[10px] tw-py-[5px]';

const generateColumns = (isPrefilled: boolean) => [
  {
    title: 'Item',
    width: '100%',
    customClasses: 'tw-min-w-[200px]',
    paddingClasses: 'tw-py-[5px] tw-pl-[10px]',
    headerClasses: headerRowClasses,
    Cell({ rowIndex, control, dropdowns }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.item`}
          control={control}
          render={({ field }) => (
            <SelectField
              // @TODO: add functionality to item dropdown on next iterations
              disabled={true}
              isSearchable={true}
              firstIsNull={true}
              selected={field.value}
              options={dropdowns.items}
              selectedSpanClasses={clsx('tw-max-w-[10em] tw-truncate')}
              onChange={field.onChange}
            />
          )}
        />
      );
    },
  },
  {
    title: 'Account',
    width: '100%',
    customClasses: 'tw-min-w-[200px]',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    HeaderCell() {
      return <Label label={'Account'} labelClasses={clsx('tw-text-cfo-black')} required={true} />;
    },
    Cell({ entry, rowIndex, control, dropdowns, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.account`}
          control={control}
          defaultValue={entry.account}
          rules={{ required: true }}
          render={({ field }) => (
            <SelectField
              id={`lineItems-${rowIndex}-account`}
              isSearchable={true}
              selected={field.value}
              options={dropdowns.accounts}
              renderOption={renderWithTypeOptions}
              onChange={field.onChange}
              classes={{ options: 'account-select' }}
              selectedSpanClasses={clsx('tw-max-w-[10em] tw-truncate')}
              showActiveIcon={false}
              isAiGenerated={
                draftLineEntries[rowIndex]?.accountIsAutomated &&
                !!draftLineEntries[rowIndex]?.account &&
                field.value?.publishData === draftLineEntries[rowIndex]?.account
              }
              isManualExpensePrefilled={isPrefilled}
            />
          )}
        />
      );
    },
  },
  {
    title: 'Description',
    width: '100%',
    customClasses: 'tw-min-w-[300px]',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    Cell({ entry, rowIndex, control, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.description`}
          control={control}
          defaultValue={entry.description}
          render={({ field }) => {
            return (
              <InputField
                placeholder="Type here"
                registration={field}
                value={field.value}
                maxLength={100}
                containerClassName={clsx('tw-mb-0')}
                isAiGenerated={
                  draftLineEntries[rowIndex]?.descriptionIsAutomated &&
                  !!draftLineEntries[rowIndex]?.description &&
                  field.value === draftLineEntries[rowIndex]?.description
                }
                isManualExpensePrefilled={isPrefilled}
              />
            );
          }}
        />
      );
    },
  },
  {
    title: 'Tax',
    width: '100%',
    customClasses: 'tw-min-w-[150px]',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    HeaderCell: ({ settings }) => {
      return (
        <Label
          label={'Tax'}
          labelClasses={clsx(!settings.isTaxRequired && 'disabled', 'tw-text-cfo-black')}
          required={settings.isTaxRequired}
        />
      );
    },
    Cell: ({ rowIndex, control, dropdowns, settings, draftLineEntries, events }) => {
      return (
        <Controller
          name={`lineItems.${rowIndex}.taxCode`}
          control={control}
          rules={{
            validate: (value: string, record: FieldValues) => {
              const isTaxRequired = getIsTaxRequired(record.taxCalculation);
              return isTaxRequired ? !!value : true;
            },
          }}
          render={({ field }) => (
            <SelectField
              id={`lineItems-${rowIndex}-taxCode`}
              isSearchable={true}
              selected={field.value}
              options={dropdowns.taxCodes}
              onChange={(value) => {
                field.onChange(value);
                if (events?.trigger) {
                  events.trigger();
                }
              }}
              disabled={!settings.isTaxRequired}
              tooltip={getSelectTaxtooltip(rowIndex, settings.isTaxRequired)}
              selectedSpanClasses={clsx('tw-max-w-[7em] tw-truncate')}
              isAiGenerated={
                draftLineEntries[rowIndex]?.taxCodeIsAutomated &&
                !!draftLineEntries[rowIndex]?.taxCode &&
                field.value?.value === draftLineEntries[rowIndex]?.taxCode?.id
              }
              isManualExpensePrefilled={isPrefilled}
            />
          )}
        />
      );
    },
  },
  {
    title: 'Qty',
    width: '100%',
    customClasses: 'tw-min-w-[120px]',
    textAlignment: 'tw-text-right',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    HeaderCell() {
      return (
        <Label
          label="Qty"
          labelClasses={clsx('tw-text-cfo-black')}
          mainContainerClasses={clsx('tw-justify-end')}
          required={true}
        />
      );
    },
    Cell({ rowIndex, control, events, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.qty`}
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <NumericInputField
                containerClassName={clsx('tw-mb-0')}
                placeholder="0.00"
                maxLength={20}
                maskOptions={{
                  allowNegative: false,
                  valueIsNumericString: true,
                }}
                onChange={(e) => {
                  e.target.classList.remove('!tw-bg-cfo-blue-200');
                  field.onChange(e);
                }}
                value={field.value}
                onBlur={() => {
                  events.onQuantityBlur(rowIndex, field.value);
                  field.onBlur();
                }}
                isAiGenerated={
                  draftLineEntries[rowIndex]?.qtyIsAutomated &&
                  !!draftLineEntries[rowIndex]?.qty &&
                  field.value === Number(draftLineEntries[rowIndex]?.qty).toFixed(2)
                }
                isManualExpensePrefilled={isPrefilled}
              />
            );
          }}
        />
      );
    },
  },
  {
    title: 'Unit Price',
    width: '100%',
    customClasses: 'tw-min-w-[170px]',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    HeaderCell() {
      return (
        <Label
          label={'Unit Price'}
          labelClasses={clsx('tw-text-cfo-black')}
          mainContainerClasses={clsx('tw-justify-end')}
          required={true}
        />
      );
    },
    Cell({ rowIndex, control, events, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.unitPrice`}
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <NumericInputField
              containerClassName={clsx('tw-mb-0')}
              placeholder="0.00"
              maxLength={20}
              maskOptions={{
                valueIsNumericString: true,
              }}
              value={field.value}
              onChange={(e) => {
                e.target.classList.remove('!tw-bg-cfo-blue-200');
                field.onChange(e);
              }}
              onBlur={() => {
                events.onUnitPriceBlur(rowIndex, field.value);
                field.onBlur();
              }}
              isAiGenerated={
                draftLineEntries[rowIndex]?.totalAmountIsAutomated &&
                !!draftLineEntries[rowIndex]?.totalAmount &&
                field.value === Number(draftLineEntries[rowIndex]?.totalAmount).toFixed(2)
              }
              isManualExpensePrefilled={isPrefilled}
            />
          )}
        />
      );
    },
  },
  {
    title: 'Amount',
    width: '100%',
    customClasses: 'tw-min-w-[170px]',
    textAlignment: 'tw-text-right',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    Cell({ entry, rowIndex, control, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.totalAmount`}
          control={control}
          rules={{ required: true }}
          defaultValue={entry.totalAmount}
          render={({ field }) => (
            <NumericInputField
              containerClassName={clsx('tw-mb-0')}
              placeholder="0.00"
              maxLength={20}
              readOnly
              maskOptions={{
                valueIsNumericString: true,
                decimalScale: 2,
              }}
              value={field.value}
              isAiGenerated={
                draftLineEntries[rowIndex]?.totalAmountIsAutomated &&
                !!draftLineEntries[rowIndex]?.totalAmount &&
                field.value === Number(draftLineEntries[rowIndex]?.totalAmount).toFixed(2)
              }
              isManualExpensePrefilled={isPrefilled}
            />
          )}
        />
      );
    },
  },
  {
    title: 'Class',
    width: '100%',
    customClasses: 'tw-min-w-[150px]',
    textAlignment: 'tw-text-center',
    paddingClasses: paddingClasses,
    headerClasses: headerRowClasses,
    Cell({ rowIndex, control, dropdowns, draftLineEntries }) {
      return (
        <Controller
          name={`lineItems.${rowIndex}.class`}
          control={control}
          render={({ field }) => (
            <SelectField
              id={`lineItems-${rowIndex}-class`}
              isSearchable={true}
              firstIsNull={true}
              selected={field.value}
              options={dropdowns.classes}
              onChange={field.onChange}
              selectedSpanClasses={clsx('tw-max-w-[7em] tw-truncate')}
              isAiGenerated={
                draftLineEntries[rowIndex]?.classIsAutomated &&
                !!draftLineEntries[rowIndex]?.class &&
                field.value?.publishData === draftLineEntries[rowIndex]?.class
              }
              isManualExpensePrefilled={isPrefilled}
            />
          )}
        />
      );
    },
  },
  {
    width: '20%',
    customClasses: 'tw-min-w-[30px]',
    textAlignment: 'text-center',
    paddingClasses: 'tw-py-[5px] tw-pl-[5px] tw-pr-[10px]',
    headerClasses: headerRowClasses,
    Cell({ rowIndex, events }) {
      return (
        <Button
          className={clsx('tw-w-full', 'tw-justify-center', 'tw-m-0 tw-p-0')}
          onClick={() => events.onRemove(rowIndex)}
        >
          <Icon
            icon={ICONS.delete}
            className={clsx(
              'tw-text-cfo-black',
              'tw-rounded-[50%]',
              'hover:tw-text-cfo-red-400 hover:tw-bg-cfo-red-100'
            )}
          />
        </Button>
      );
    },
  },
];

type LineItemsTableProps = {
  fields: FieldArrayWithId<LineItemsForm, 'lineItems', 'id'>[];
  register: UseFormRegister<LineItemsForm>;
  control: Control<LineItemsForm>;
  dropdowns: RemoteOptions['lineItems'];
  events: TableEvents;
  taxCalculation: LineItemsForm['taxCalculation'];
  draftLineEntries: LineEntryForm[];
  isPrefilled?: boolean;
};

const LineItemsTable = ({
  fields,
  register,
  control,
  dropdowns,
  events,
  taxCalculation,
  draftLineEntries,
  isPrefilled = false,
}: LineItemsTableProps) => {
  const settings = {
    isTaxRequired: getIsTaxRequired(taxCalculation),
  };

  const rowProps = {
    register,
    control,
    dropdowns,
    events,
    settings,
    draftLineEntries,
  };

  const columnProps = {
    settings,
  };

  const columns = React.useMemo(() => generateColumns(isPrefilled), [isPrefilled]);

  return (
    <Table
      data={fields}
      columns={columns}
      rowProps={rowProps}
      columnProps={columnProps}
      // Line Items Table Tailwind Classes
      containerClassName={clsx('tw-border-0')}
      rowClassName={clsx('tw-border-none')}
    />
  );
};

export default LineItemsTable;
